import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Mic } from 'lucide-react';

const PLAYBACK_RATES = [1, 1.5, 2];

export default function VoiceMessagePlayer({ url, duration, isMine }) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [rateIndex, setRateIndex] = useState(0);
  const [waveform, setWaveform] = useState([]);
  const audioRef = useRef(null);
  const animRef = useRef(null);

  const formatTime = (secs) => {
    const s = Math.round(secs);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  // Generate static waveform from audio
  useEffect(() => {
    // Generate random-ish but consistent waveform bars
    const bars = [];
    const seed = url ? url.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 42;
    for (let i = 0; i < 30; i++) {
      const v = (Math.sin(seed * (i + 1) * 0.7) + 1) / 2;
      bars.push(0.2 + v * 0.8);
    }
    setWaveform(bars);
  }, [url]);

  const updateTime = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (!audioRef.current.paused) {
        animRef.current = requestAnimationFrame(updateTime);
      }
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      cancelAnimationFrame(animRef.current);
      setPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setPlaying(true);
        updateTime();
      }).catch(() => {});
    }
  }, [playing, updateTime]);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(0);
    cancelAnimationFrame(animRef.current);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current && audioRef.current.duration && isFinite(audioRef.current.duration)) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const cycleRate = useCallback(() => {
    const next = (rateIndex + 1) % PLAYBACK_RATES.length;
    setRateIndex(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = PLAYBACK_RATES[next];
    }
  }, [rateIndex]);

  const handleScrub = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const time = x * (audioDuration || 1);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, [audioDuration]);

  const progress = audioDuration > 0 ? currentTime / audioDuration : 0;

  useEffect(() => {
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
      minWidth: 200, maxWidth: 280,
    }}>
      <audio ref={audioRef} src={url} preload="metadata"
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
      />

      {/* Play/pause button */}
      <button onClick={togglePlay} style={{
        width: 32, height: 32, borderRadius: '50%', border: 'none',
        background: isMine ? 'rgba(255,255,255,0.2)' : 'var(--c-brand)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
      }}>
        {playing ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 2 }} />}
      </button>

      {/* Waveform + scrubber */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={handleScrub}
          style={{
            display: 'flex', alignItems: 'center', gap: 1, height: 24, cursor: 'pointer',
            position: 'relative',
          }}
        >
          {waveform.map((v, i) => {
            const barProgress = i / waveform.length;
            const isPlayed = barProgress <= progress;
            return (
              <div key={i} style={{
                width: 3, borderRadius: 1.5, flex: '0 0 auto',
                height: Math.max(4, v * 22),
                background: isPlayed
                  ? (isMine ? '#fff' : 'var(--c-brand)')
                  : (isMine ? 'rgba(255,255,255,0.3)' : 'var(--c-border)'),
                transition: 'background 0.1s',
              }} />
            );
          })}
        </div>

        {/* Time + speed */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
          <span style={{
            fontSize: 10, color: isMine ? 'rgba(255,255,255,0.7)' : 'var(--c-text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {playing ? formatTime(currentTime) : formatTime(audioDuration)}
          </span>
          <button onClick={cycleRate} style={{
            fontSize: 10, fontWeight: 600, padding: '0 4px', border: 'none', background: 'none',
            color: isMine ? 'rgba(255,255,255,0.7)' : 'var(--c-text-muted)',
            cursor: 'pointer',
          }}>
            {PLAYBACK_RATES[rateIndex]}x
          </button>
        </div>
      </div>

      {/* Mic icon */}
      <Mic size={14} style={{
        color: isMine ? 'rgba(255,255,255,0.4)' : 'var(--c-text-muted)',
        flexShrink: 0,
        opacity: 0.5,
      }} />
    </div>
  );
}
